import * as React from 'react';
import { useNavigate } from 'react-router';
import { alpha, useTheme } from '@mui/material/styles';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAuth } from '../../context';
import { AppDialog } from '../common/AppDialog';
import { CustomButton } from '../common/CustomButton';
import { getButtonColor } from '../../theme/themeUtils';
import {
    responsiveFontSize,
    responsiveIconSize,
    stackedActionButtonFixedWidthXsNarrowSx,
    stackedActionButtonMinHeightSx,
} from '../../util/responsiveUtils';

const HowItWorksModal = React.lazy(() =>
    import('../footer/HowItWorksModal').then((m) => ({ default: m.HowItWorksModal })),
);

function dispatchOpenSupportChat() {
    window.dispatchEvent(new CustomEvent('openChat', { detail: { supportNewHelp: true } }));
}

interface LoggedOutHelpModalProps {
    open: boolean;
    onClose: () => void;
    onContactUs: () => void;
    onReadFaq: () => void;
    onHelpCentre: () => void;
}

function LoggedOutHelpModal({
    open,
    onClose,
    onContactUs,
    onReadFaq,
    onHelpCentre,
}: LoggedOutHelpModalProps) {
    const theme = useTheme();

    const stackSx = {
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        pt: 0.5,
    } as const;

    const buttonStretchSx = {
        ...stackedActionButtonFixedWidthXsNarrowSx,
        minHeight: stackedActionButtonMinHeightSx,
        width: '100%',
        maxWidth: 'none',
        alignSelf: 'stretch',
        fontSize: responsiveFontSize.body,
        fontWeight: 600,
    } as const;

    return (
        <AppDialog open={open} onClose={onClose} title="Get help" maxWidth="sm" fullWidth>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: responsiveFontSize.body, mb: 1 }}>
                Learn how Virtoly works, browse the help centre, or contact us.
            </Typography>
            <Stack sx={stackSx}>
                <CustomButton
                    type="button"
                    variant="outlined"
                    size="medium"
                    onClick={onContactUs}
                    color={getButtonColor(theme)}
                    textColor={getButtonColor(theme)}
                    sx={buttonStretchSx}
                >
                    Contact Us
                </CustomButton>
                <CustomButton
                    type="button"
                    variant="outlined"
                    size="medium"
                    onClick={onReadFaq}
                    color={getButtonColor(theme)}
                    textColor={getButtonColor(theme)}
                    sx={buttonStretchSx}
                >
                    Read FAQ
                </CustomButton>
                <CustomButton
                    type="button"
                    variant="outlined"
                    size="medium"
                    onClick={onHelpCentre}
                    color={getButtonColor(theme)}
                    textColor={getButtonColor(theme)}
                    sx={buttonStretchSx}
                >
                    Help Centre
                </CustomButton>
            </Stack>
        </AppDialog>
    );
}

export interface HeaderHelpTriggerProps {
    onClick: () => void;
}

/** Header “Help” control: compact assist chip aligned with Virtoly header chrome. */
export function HeaderHelpTrigger({ onClick }: HeaderHelpTriggerProps) {
    const theme = useTheme();
    const accent = getButtonColor(theme);

    return (
        <Tooltip title="Help & support — chat, guides, or contact" enterDelay={400}>
            <Button
                variant="text"
                onClick={onClick}
                aria-label="Help and support"
                startIcon={
                    <HelpOutlineIcon
                        sx={{
                            fontSize: responsiveIconSize.sm,
                            color: accent,
                            opacity: theme.palette.mode === 'dark' ? 0.95 : 0.88,
                        }}
                    />
                }
                sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: responsiveFontSize.bodySmall,
                    letterSpacing: 0.01,
                    minWidth: 'auto',
                    flexShrink: 0,
                    borderRadius: 2,
                    px: { xs: 1, sm: 1.25 },
                    py: { xs: 0.5, sm: 0.625 },
                    border: `1px solid ${alpha(accent, theme.palette.mode === 'dark' ? 0.28 : 0.22)}`,
                    bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.1 : 0.06),
                    transition: 'background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease',
                    boxShadow: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    '& .MuiButton-startIcon': {
                        marginRight: 0.375,
                        marginLeft: 0,
                    },
                    '@media (hover: hover)': {
                        '&:hover': {
                            bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.11),
                            borderColor: alpha(accent, theme.palette.mode === 'dark' ? 0.42 : 0.34),
                            boxShadow: `0 2px 10px ${alpha(accent, 0.14)}`,
                        },
                    },
                    '&:active': {
                        bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.2 : 0.14),
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${accent}`,
                        outlineOffset: 2,
                    },
                }}
            >
                Help
            </Button>
        </Tooltip>
    );
}

export type UseNeedHelpFlowOptions = {
    /** When a logged-out user opens FAQ from this flow, which “How does it work?” tab to show (0 = customer, 1 = expert). */
    faqInitialTab?: number;
    /** Optional accordion section to expand in the FAQ modal. */
    faqInitialSection?: string;
};

export function useNeedHelpFlow(options?: UseNeedHelpFlowOptions) {
    const auth = useAuth();
    const navigate = useNavigate();
    const faqInitialTab = options?.faqInitialTab;
    const faqInitialSection = options?.faqInitialSection;
    const [loggedOutHelpOpen, setLoggedOutHelpOpen] = React.useState(false);
    const [faqOpen, setFaqOpen] = React.useState(false);

    const openNeedHelp = React.useCallback(() => {
        if (auth.isLogin()) {
            dispatchOpenSupportChat();
        } else {
            setLoggedOutHelpOpen(true);
        }
    }, [auth]);

    const handleContactUs = React.useCallback(() => {
        setLoggedOutHelpOpen(false);
        navigate('/contactUs');
    }, [navigate]);

    const handleReadFaq = React.useCallback(() => {
        setLoggedOutHelpOpen(false);
        setFaqOpen(true);
    }, []);

    const handleHelpCentre = React.useCallback(() => {
        setLoggedOutHelpOpen(false);
        navigate('/help-center');
    }, [navigate]);

    const needHelpModals = (
        <>
            <LoggedOutHelpModal
                open={loggedOutHelpOpen}
                onClose={() => setLoggedOutHelpOpen(false)}
                onContactUs={handleContactUs}
                onReadFaq={handleReadFaq}
                onHelpCentre={handleHelpCentre}
            />
            {faqOpen && (
                <React.Suspense fallback={null}>
                    <HowItWorksModal
                        open={faqOpen}
                        onClose={() => setFaqOpen(false)}
                        initialTab={faqInitialTab}
                        initialSection={faqInitialSection}
                    />
                </React.Suspense>
            )}
        </>
    );

    return { openNeedHelp, needHelpModals };
}
